import { CreateCustomerRequestDTO } from "@/gen/api"
import { councilCustomerApi } from "@/lib/api"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import axios from "axios"

export function extractErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data
    if (data && typeof data === "object" && "message" in data) {
      return String((data as { message: unknown }).message)
    }
    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data)
        if (parsed.message) return parsed.message
      } catch {
        /* ignore */
      }
    }
    return error.message || "An error occurred"
  }
  if (error instanceof Error) return error.message
  return "An error occurred"
}

export type CreateCustomerResult =
  | { success: true; data: unknown }
  | { success: false; errorMessage: string }

export type UseCreateCustomerParams = {
  onSuccess?: (data: unknown) => void
  onError?: (message: string) => void
}

const useCreateCustomer = (params: UseCreateCustomerParams = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      councilId: string
      req: CreateCustomerRequestDTO
    }): Promise<CreateCustomerResult> => {
      try {
        const response = await councilCustomerApi.createCustomer(data.councilId, data.req)
        return { success: true, data: response.data }
      } catch (error) {
        return { success: false, errorMessage: extractErrorMessage(error) }
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ["customers"] })
        queryClient.invalidateQueries({ queryKey: ["recent-customers"] })
        params.onSuccess?.(result.data)
      } else {
        params.onError?.(result.errorMessage)
      }
    },
  })
}

export default useCreateCustomer
